import React from 'react';
import { Link } from 'react-router-dom';
import { 
  Car, Bike, Ship, Anchor, Truck, Zap, Coffee, ChevronLeft
} from 'lucide-react';
import { Button } from '../components/ui/button';
import VehicleSearch from './VehicleSearch';

// Slug-to-category mapping (URL path → vehicle_category value)
const CATEGORY_MAP = {
  'cars':              { value: 'car',              label: 'Cars',              icon: Car,    color: 'from-blue-600 to-blue-800',     tagline: 'Sedans, SUVs, utes and more — hire direct from local hosts.' },
  'motorcycles':       { value: 'motorcycle',       label: 'Motorcycles',       icon: Bike,   color: 'from-orange-500 to-orange-700',  tagline: 'Dirt bikes, cruisers, and sport bikes ready to ride.' },
  'boats':             { value: 'boat',             label: 'Boats',             icon: Ship,   color: 'from-cyan-600 to-cyan-800',      tagline: 'Runabouts, pontoons, and fishing boats for your next water adventure.' },
  'jetskis':           { value: 'jetski',           label: 'Jet Skis',          icon: Anchor, color: 'from-teal-500 to-teal-700',      tagline: 'High-performance jet skis for weekend thrills on the water.' },
  'trailers':          { value: 'trailer',          label: 'Trailers',          icon: Truck,  color: 'from-slate-600 to-slate-800',    tagline: 'Box trailers, flatbeds, car carriers and more for any haul.' },
  'ebikes':            { value: 'ebike',            label: 'eBikes',            icon: Zap,    color: 'from-green-500 to-green-700',    tagline: 'Electric bikes for commuting, exploring, or just cruising around.' },
  'electric-scooters': { value: 'electric_scooter', label: 'Electric Scooters', icon: Zap,    color: 'from-violet-500 to-violet-700',  tagline: 'Zippy electric scooters perfect for short trips and last-mile transport.' },
  'mobility-scooters': { value: 'mobility_scooter', label: 'Mobility Scooters', icon: Zap,    color: 'from-rose-500 to-rose-700',      tagline: 'Accessible mobility scooters for airports, cruises, and everyday transport.' },
  'specialty-assets':  { value: 'specialty_asset',  label: 'Specialty / Income-Generating Assets', icon: Coffee, color: 'from-amber-600 to-amber-800', tagline: 'Coffee trailers, event setups, and other specialty assets for private functions and controlled use.' },
};

const CategoryLandingPage = ({ slug }) => {
  const config = CATEGORY_MAP[slug];

  // Fallback for unknown slugs
  if (!config) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-slate-900 mb-2">Category not found</h1>
          <p className="text-slate-500 mb-4">This category doesn't exist.</p>
          <Link to="/vehicles">
            <Button className="bg-[#004B8D] hover:bg-[#003a6d]">Browse All Vehicles</Button>
          </Link>
        </div>
      </div>
    );
  }

  const IconComponent = config.icon;

  return (
    <div className="min-h-screen bg-slate-50" data-testid={`category-landing-${slug}`}>
      {/* Hero Section */}
      <div className={`bg-gradient-to-r ${config.color} text-white`}>
        <div className="max-w-7xl mx-auto px-4 py-10 sm:py-14">
          <div className="flex items-center gap-3 mb-4">
            <Link to="/vehicles">
              <Button variant="ghost" size="icon" className="h-9 w-9 text-white/80 hover:text-white hover:bg-white/10">
                <ChevronLeft className="w-5 h-5" />
              </Button>
            </Link>
            <div className="w-12 h-12 rounded-xl bg-white/15 backdrop-blur-sm flex items-center justify-center">
              <IconComponent className="w-6 h-6" />
            </div>
          </div>
          <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-bold mb-2" data-testid="category-hero-title">
            {config.label} for Hire
          </h1>
          <p className="text-white/80 text-sm sm:text-base max-w-xl">
            {config.tagline}
          </p>
        </div>
      </div>

      {/* Pre-filtered Search */}
      <VehicleSearch defaultCategory={config.value} hideHeader={true} />
    </div>
  );
};

// Export the map for use in other components (e.g., landing page)
export { CATEGORY_MAP };
export default CategoryLandingPage;
